import React, { useState, useEffect } from "react";
import { AreaChart, BarChart, LineChart } from '@tremor/react';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import MultilineChartIcon from '@mui/icons-material/MultilineChart';

const RevenueChart = () => {
    const [monthlyRevenueData, setMonthlyRevenueData] = useState([]);
    const [revenueLoading, setRevenueLoading] = useState(true);
    const [revenueError, setRevenueError] = useState(null);
    const [chartType, setChartType] = useState(() => {
        return localStorage.getItem('admin-chart-type') || 'bar';
    });
    const [screenSize, setScreenSize] = useState('lg');

    // Add custom styles for animations and tooltip
    const customStyles = `
        .animation-delay-150 {
            animation-delay: 150ms;
        }
        .hover\\:scale-102:hover {
            transform: scale(1.02);
        }
        .tremor-chart {
            background: transparent !important;
        }
        .tremor-AreaChart-gradient {
            opacity: 0.3 !important;
        }
        .tremor-Tooltip {
            background: rgba(0, 0, 0, 0.8) !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            border-radius: 12px !important;
            backdrop-filter: blur(10px) !important;
            color: white !important;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
        }
        .tremor-Tooltip-content {
            color: white !important;
        }
        .tremor-Tooltip-content > * {
            color: white !important;
        }
        .recharts-tooltip-wrapper {
            filter: none !important;
        }
        .recharts-default-tooltip {
            background: rgba(0, 0, 0, 0.8) !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            border-radius: 12px !important;
            backdrop-filter: blur(10px) !important;
            color: white !important;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
        }
        .recharts-tooltip-label {
            color: white !important;
        }
        .recharts-tooltip-item {
            color: white !important;
        }
        .recharts-tooltip-item-name {
            color: white !important;
        }
        .recharts-tooltip-item-value {
            color: white !important;
        }
        .recharts-cartesian-axis-tick-value {
            fill: white !important;
        }
        .recharts-text {
            fill: white !important;
        }
    `;

    React.useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = customStyles;
        document.head.appendChild(styleElement);
        return () => document.head.removeChild(styleElement);
    }, [customStyles]);

    // Handle screen size detection
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            if (width < 640) {
                setScreenSize('sm');
            } else if (width < 1024) {
                setScreenSize('md');
            } else {
                setScreenSize('lg');
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch monthly revenue data
    useEffect(() => {
        const fetchMonthlyRevenue = async () => {
            try {
                setRevenueLoading(true);

                const token = localStorage.getItem('authToken');
                if (!token) {
                    throw new Error('Authentication required. Please login.');
                }

                const response = await fetch('/rest/Dashboard/getMonthlyRevenue', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    if (response.status === 401) {
                        throw new Error('Authentication failed. Please login again.');
                    }
                    throw new Error(`API Error: ${response.status} - ${response.statusText}`);
                }

                const data = await response.json();
                setMonthlyRevenueData(data);
                setRevenueError(null);
            } catch (error) {
                console.error('Error fetching monthly revenue:', error);
                setRevenueError(error.message);
            } finally {
                setRevenueLoading(false);
            }
        };

        fetchMonthlyRevenue();
    }, []);

    // Save chart type preference
    useEffect(() => {
        localStorage.setItem('admin-chart-type', chartType);
    }, [chartType]);

    const chartTypes = [
        { value: 'bar', label: 'Bar Chart', icon: BarChartIcon },
        { value: 'line', label: 'Line Chart', icon: ShowChartIcon },
        { value: 'area', label: 'Area Chart', icon: MultilineChartIcon }
    ];

    const renderChart = () => {
        if (revenueLoading) {
            return (
                <div className="h-80 flex items-center justify-center">
                    <div className="text-center">
                        <div className="relative">
                            <div className="w-16 h-16 border-4 border-white/20 border-t-emerald-500 rounded-full animate-spin"></div>
                            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-blue-500 rounded-full animate-spin animation-delay-150"></div>
                        </div>
                        <div className="mt-6 text-white/80 font-medium">Loading chart data...</div>
                        <div className="mt-2 text-white/50 text-sm">Fetching real-time revenue data</div>
                    </div>
                </div>
            );
        }

        if (revenueError) {
            return (
                <div className="h-80 flex items-center justify-center">
                    <div className="text-center max-w-md">
                        <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <div className="text-red-400 font-semibold mb-2">Connection Error</div>
                        <div className="text-white/60 text-sm leading-relaxed">{revenueError}</div>
                        <button
                            onClick={() => window.location.reload()}
                            className="mt-4 px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 font-medium"
                        >
                            Retry Connection
                        </button>
                    </div>
                </div>
            );
        }

        if (!monthlyRevenueData || monthlyRevenueData.length === 0) {
            return (
                <div className="h-80 flex items-center justify-center">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-600 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                        </div>
                        <div className="text-white/70 font-medium">No Revenue Data</div>
                        <div className="text-white/50 text-sm mt-2">No data available for the selected period</div>
                    </div>
                </div>
            );
        }

        // Ensure all 12 months are displayed
        const allMonths = [
            'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
            'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
        ];

        const fullData = allMonths.map(month => {
            const existingData = monthlyRevenueData.find(item => item.month === month);
            return {
                month: month,
                revenue: existingData ? existingData.revenue : 0
            };
        });

        // Group months based on screen size for responsive design
        const getGroupedData = () => {
            if (screenSize === 'sm') {
                // Group every 3 months for small screens
                const grouped = [];
                for (let i = 0; i < fullData.length; i += 3) {
                    const group = fullData.slice(i, i + 3);
                    const totalRevenue = group.reduce((sum, item) => sum + item.revenue, 0);
                    const monthNames = group.map(item => item.month).join('-');
                    grouped.push({
                        month: monthNames,
                        revenue: totalRevenue,
                        groupedMonths: group
                    });
                }
                return grouped;
            } else if (screenSize === 'md') {
                // Group every 2 months for medium screens
                const grouped = [];
                for (let i = 0; i < fullData.length; i += 2) {
                    const group = fullData.slice(i, i + 2);
                    const totalRevenue = group.reduce((sum, item) => sum + item.revenue, 0);
                    const monthNames = group.map(item => item.month).join('-');
                    grouped.push({
                        month: monthNames,
                        revenue: totalRevenue,
                        groupedMonths: group
                    });
                }
                return grouped;
            } else {
                // Show all months for large screens
                return fullData.map(item => ({
                    ...item,
                    groupedMonths: [item]
                }));
            }
        };

        const chartData = getGroupedData();

        const customTooltip = ({ active, payload, label }) => {
            if (active && payload && payload.length) {
                // Find the data point that matches the label
                const dataPoint = chartData.find(item => item.month === label);
                const groupedMonths = dataPoint?.groupedMonths || [];

                return (
                    <div className="bg-black/90 backdrop-blur-sm border border-white/20 rounded-xl p-4 shadow-2xl max-w-xs">
                        <p className="text-white font-medium text-sm mb-3">{label}</p>
                        {groupedMonths.length > 1 ? (
                            // Show breakdown for grouped months
                            <div className="space-y-2">
                                {groupedMonths.map((month, index) => (
                                    <div key={index} className="flex justify-between items-center">
                                        <span className="text-white/80 text-sm">{month.month}:</span>
                                        <span className="text-emerald-400 font-medium text-sm">
                                            €{month.revenue.toLocaleString()}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-t border-white/20 pt-2 mt-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-white font-medium text-sm">Total:</span>
                                        <span className="text-emerald-400 font-semibold text-base">
                                            €{payload[0].value.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            // Show single month
                            <p className="text-emerald-400 font-semibold text-base">
                                Revenue: €{payload[0].value.toLocaleString()}
                            </p>
                        )}
                    </div>
                );
            }
            return null;
        };

        const commonProps = {
            data: chartData,
            index: "month",
            categories: ["revenue"],
            colors: ["emerald"],
            className: "h-80",
            showLegend: false,
            showGridLines: true,
            showXAxis: true,
            showYAxis: true,
            enableLegendSlider: false,
            showAnimation: true,
            valueFormatter: (value) => `€${value.toLocaleString()}`,
            customTooltip: customTooltip
        };

        switch (chartType) {
            case 'line':
                return <LineChart {...commonProps} />;
            case 'area':
                return <AreaChart {...commonProps} />;
            default:
                return <BarChart {...commonProps} />;
        }
    };

    return (
        <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-8 gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <BarChartIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                            Aylık Gelir Özeti
                        </h2>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    {/* Chart Type Selector */}
                    <div className="flex items-center gap-3">
                        <div className="flex bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-1 shadow-lg">
                            {chartTypes.map(type => {
                                const IconComponent = type.icon;
                                return (
                                    <button
                                        key={type.value}
                                        onClick={() => setChartType(type.value)}
                                        className={`px-4 py-2 text-sm flex items-center gap-2 transition-all duration-300 rounded-xl font-medium ${
                                            chartType === type.value
                                                ? 'bg-gradient-to-r from-emerald-500 to-blue-500 text-white shadow-lg transform scale-105'
                                                : 'text-white/60 hover:text-white hover:bg-white/10 hover:scale-102'
                                        }`}
                                        title={type.label}
                                    >
                                        <IconComponent className="w-4 h-4" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                {renderChart()}
            </div>
        </div>
    );
};

export default RevenueChart;